import { X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useState, useMemo, useCallback } from 'react'

import { AdasBox, AdasButton, AdasDatePicker, AdasModal, AdasPaper, AdasTypography } from '@components/wrapper-components'
import { BUTTONS_DISPLAY_LABEL, DATE_FORMATS } from '../../../constants'
import { formatDate } from '../../../utils/utils'

export const DatePickerModal = ({
  open,
  handleClose,
  title,
  cancelLabel = BUTTONS_DISPLAY_LABEL.CANCEL,
  confirmLabel,
  message,
  dateRangeLimit = 2,
  handleSubmit,
  dateRangeLimitWarning
}) => {
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [errorMessage, setErrorMessage] = useState()

  const handleCloseModal = useCallback(() => {
    setStartDate('')
    setEndDate('')
    setErrorMessage('')
    handleClose()
  }, [setStartDate, setEndDate, setErrorMessage, handleClose])

  const handleSubmitModal = useCallback(
    (startDate, endDate) => {
      handleSubmit(startDate, endDate).then(() => {
        // Clear fields after successful submission
        setStartDate('')
        setEndDate('')
        setErrorMessage('')
      })
    },
    [handleSubmit, setStartDate, setEndDate, setErrorMessage]
  )

  const formattedStartDate = useMemo(() => formatDate(startDate, DATE_FORMATS.ISO_DATE_STRING), [startDate])
  const formattedEndDate = useMemo(() => formatDate(endDate, DATE_FORMATS.ISO_DATE_STRING), [endDate])

  const handleStartDateChange = useCallback(
    (newDate) => {
      const startDate = new Date(newDate)
      const endDateTemp = new Date(endDate)

      //Setting the max date range with in limit
      const maxSelectableEndDate = new Date(startDate)
      maxSelectableEndDate.setMonth(maxSelectableEndDate.getMonth() + dateRangeLimit)
      maxSelectableEndDate.setHours(23, 59, 59, 999)

      if (endDateTemp && endDateTemp <= startDate) {
        setErrorMessage('Start date must be less than end date')
      } else if (endDateTemp && endDateTemp >= maxSelectableEndDate) {
        setErrorMessage(dateRangeLimitWarning)
      } else {
        setErrorMessage('') // doing this since date picker shows the last clicked date even it's not set in state
      }
      let formattedDate = formatDate(startDate)
      setStartDate(formattedDate)
    },
    [endDate]
  )

  const handleEndDateChange = useCallback(
    (newDate) => {
      const endDate = new Date(newDate).setHours(23, 59, 59, 999)
      const startDateTemp = new Date(startDate)

      //Setting the max date range with in limit
      const maxSelectableEndDate = new Date(startDate)
      maxSelectableEndDate.setMonth(maxSelectableEndDate.getMonth() + dateRangeLimit)

      if (startDateTemp && endDate <= startDateTemp) {
        setErrorMessage('End date must be greater than start date')
      } else if (maxSelectableEndDate && endDate > maxSelectableEndDate) {
        setErrorMessage(dateRangeLimitWarning)
      } else {
        setErrorMessage('')
      }
      const formattedDate = formatDate(endDate)
      setEndDate(formattedDate)
    },
    [startDate]
  )

  return (
    <AdasModal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      className='flex items-center justify-center'
    >
      <AdasPaper variant='outlined' className='flex flex-col relative p-[20px] min-h-[200px]'>
        <AdasBox className='flex justify-between mb-3'>
          <AdasTypography variant='h6' className='font-bold text-lg leading-[28px] mt-0'>
            {title}
          </AdasTypography>
          <AdasButton buttonType='iconButton' className='text-info text-base cursor-pointer p-0.5' onClick={handleCloseModal}>
            <X />
          </AdasButton>
        </AdasBox>
        <AdasTypography className='mb-[20px]'>{message}</AdasTypography>
        <AdasBox className='flex justify-end mt-[20px]'>
          <AdasDatePicker
            label='Start Date'
            variant='outlined'
            name='start_date'
            disableFuture={true}
            className='flex-1 mr-4'
            value={formattedStartDate}
            format={DATE_FORMATS.ISO_DATE_STRING}
            onChange={handleStartDateChange}
          />
          <AdasDatePicker
            disableFuture={true}
            label='End Date'
            variant='outlined'
            name='end_date'
            className='flex-1 mr-4'
            value={formattedEndDate}
            format={DATE_FORMATS.ISO_DATE_STRING}
            onChange={handleEndDateChange}
          />
        </AdasBox>
        {errorMessage && (
          <AdasTypography
            variant='body1'
            className='text-error font-normal text-base text-left leading-5 mt-[20px] tracking-[0.15000000596046448px]'
          >
            {errorMessage}
          </AdasTypography>
        )}
        <AdasBox className='flex justify-end mt-[20px]'>
          <AdasButton className='mr-[5px]' variant='outlined' onClick={handleCloseModal}>
            {cancelLabel}
          </AdasButton>
          <AdasButton
            disabled={!!errorMessage || !startDate || !endDate}
            variant='contained'
            onClick={() => handleSubmitModal(startDate, endDate)}
          >
            {confirmLabel}
          </AdasButton>
        </AdasBox>
      </AdasPaper>
    </AdasModal>
  )
}

DatePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string,
  dateRangeLimitWarning: PropTypes.string
}
